import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Badge, message } from 'antd';
import { SendOutlined, EyeOutlined } from '@ant-design/icons';
import { RootState, AppDispatch } from '../../../redux/store';
import {
  setTestEmail,
  setTestData,
  setJsonError,
  setPreviewContent,
  setActiveTab,
  resetModal,
  initializeTestData,
  sendTestEmailAsync,
} from '../store/emailTestModalSlice';
import { TestEmailModalProps } from './types.d';
import { TEST_EMAIL_MODAL_CONSTANTS } from './constants';
import { isValidJSON, getDefaultTestData, updatePreview, isValidEmail, parseTestData } from './utils/helpers';
import SetupTab from './subComponents/SetupTab';
import PreviewTab from './subComponents/PreviewTab';
import VariablesTab from './subComponents/VariablesTab';
import { StyledModal, ModalHeader, IconWrapper, StyledTabs } from './styles';

const TestEmailModal: React.FC<TestEmailModalProps> = ({
  isOpen,
  onClose,
  template = null,
  onSendTest,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    testEmail,
    testData,
    jsonError,
    previewContent,
    activeTab,
    loading,
    success,
    error,
  } = useSelector((state: RootState) => state.emailTestModal);

  useEffect(() => {
    if (template && isOpen) {
      const defaultData = getDefaultTestData(template.type);
      dispatch(initializeTestData(JSON.stringify(defaultData, null, 2)));
      dispatch(setPreviewContent(updatePreview(template, defaultData)));
    }
  }, [template, isOpen, dispatch]);

  useEffect(() => {
    if (template) {
      try {
        const parsedData = JSON.parse(testData);
        dispatch(setJsonError(''));
        dispatch(setPreviewContent(updatePreview(template, parsedData)));
      } catch {
        dispatch(setJsonError(TEST_EMAIL_MODAL_CONSTANTS.VALIDATION_MESSAGES.INVALID_JSON));
        dispatch(
          setPreviewContent({
            subject: '',
            html: '',
            text: '',
          })
        );
      }
    }
  }, [testData, template, dispatch]);

  useEffect(() => {
    if (success) {
      message.success('Test email sent successfully!');
      setTimeout(() => {
        onClose();
        dispatch(resetModal());
      }, 1500);
    }
  }, [success, onClose, dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  const handleSendTest = () => {
    if (!testEmail.trim()) {
      dispatch(setJsonError(TEST_EMAIL_MODAL_CONSTANTS.VALIDATION_MESSAGES.EMPTY_EMAIL));
      return;
    }

    if (!isValidEmail(testEmail)) {
      dispatch(setJsonError('Please enter a valid email address'));
      return;
    }

    if (!isValidJSON(testData)) {
      dispatch(
        setJsonError(TEST_EMAIL_MODAL_CONSTANTS.VALIDATION_MESSAGES.INVALID_EMAIL_JSON)
      );
      return;
    }

    const parsedData = parseTestData(testData);
    if (parsedData && template) {
      dispatch(
        sendTestEmailAsync({
          templateId: template.id,
          testEmail: testEmail.trim(),
          testData: parsedData,
        })
      );

      if (onSendTest) {
        onSendTest({
          testEmail: testEmail.trim(),
          testData: parsedData,
        });
      }
    }
  };

  const handleLoadDefaultData = () => {
    if (template) {
      const defaultData = getDefaultTestData(template.type);
      dispatch(initializeTestData(JSON.stringify(defaultData, null, 2)));
    }
  };

  const handleReset = () => {
    dispatch(resetModal());
  };

  const handleClose = () => {
    dispatch(resetModal());
    onClose();
  };

  const isFormValid = useMemo(() => {
    return testEmail.trim() && isValidJSON(testData) && !jsonError;
  }, [testEmail, testData, jsonError]);

  const tabItems = [
    {
      key: 'setup',
      label: TEST_EMAIL_MODAL_CONSTANTS.TABS.SETUP,
      children: (
        <SetupTab
          testEmail={testEmail}
          onTestEmailChange={(value) => dispatch(setTestEmail(value))}
          testData={testData}
          onTestDataChange={(value) => dispatch(setTestData(value))}
          jsonError={jsonError}
          onLoadDefaultData={handleLoadDefaultData}
          onReset={handleReset}
        />
      ),
    },
    {
      key: 'preview',
      label: TEST_EMAIL_MODAL_CONSTANTS.TABS.PREVIEW,
      children: <PreviewTab previewContent={previewContent} />,
    },
    {
      key: 'variables',
      label: TEST_EMAIL_MODAL_CONSTANTS.TABS.VARIABLES,
      children: <VariablesTab template={template} testData={testData} />,
    },
  ];

  return (
    <StyledModal
      title={
        <ModalHeader>
          <IconWrapper>
            <SendOutlined />
          </IconWrapper>
          {TEST_EMAIL_MODAL_CONSTANTS.TITLE}
          {template && (
            <Badge count={<span style={{ marginLeft: 8 }}>{template.name}</span>} />
          )}
        </ModalHeader>
      }
      open={isOpen}
      onCancel={handleClose}
      width="90%"
      style={{ maxWidth: '900px' }}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {TEST_EMAIL_MODAL_CONSTANTS.BUTTONS.CANCEL}
        </Button>,
        <Button
          key="preview"
          icon={<EyeOutlined />}
          onClick={() => dispatch(setActiveTab(1))}
        >
          {TEST_EMAIL_MODAL_CONSTANTS.BUTTONS.PREVIEW}
        </Button>,
        <Button
          key="send"
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSendTest}
          loading={loading}
          disabled={!isFormValid}
        >
          {TEST_EMAIL_MODAL_CONSTANTS.BUTTONS.SEND_TEST_EMAIL}
        </Button>,
      ]}
    >
      <StyledTabs activeKey={String(activeTab)} onChange={(key) => dispatch(setActiveTab(Number(key)))} items={tabItems} />
    </StyledModal>
  );
};

export default TestEmailModal;
